import React from 'react';
import {
  Box, Chip, Divider, Drawer, IconButton, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RouteIcon from '@mui/icons-material/Route';
import FlagIcon from '@mui/icons-material/Flag';

import { lifecycleMeta } from './mechanismMeta';
import { useDisplayNames } from '../../utils/displayNames';

const humanizeLead = (bins, intervalSeconds) => {
  if (!bins || !intervalSeconds) return `${bins || 0} bins`;
  const seconds = bins * intervalSeconds;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 48 * 3600) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} d`;
};

const MEDIATION_META = {
  full: {
    label: 'Full mediation',
    tone: 'info',
    help: 'The upstream source acts entirely through the mediator — the mediator already carries its information into the model.',
  },
  partial: {
    label: 'Partial mediation',
    tone: 'warning',
    help: 'The upstream source influences the goal both through the mediator and directly — it keeps its own edge.',
  },
};

/**
 * Chain visualization for one multi-hop pathway (spec §7 J3, M13):
 * hop cards (lag, strength), total lead time, mediation narrative and the
 * mediation test evidence.
 */
const PathDrawer = ({ path, goal, onClose }) => {
  const nameOf = useDisplayNames();
  const life = lifecycleMeta(path?.lifecycle);
  const med = MEDIATION_META[path?.mediation] || null;
  const interval = goal?.interval_seconds;
  const hops = path?.hops || [];

  return (
    <Drawer anchor="right" open={Boolean(path)} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 420 }, maxWidth: '100vw', p: 2.5, overflowY: 'auto' }}>
        {path && (
          <>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <RouteIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, maxWidth: 300 }}>
                  {[...hops.map((h) => nameOf(h.source_key)), nameOf(goal?.target_key)].join(' → ')}
                </Typography>
              </Stack>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1, mb: 2 }}>
              <Chip size="small" color={life.tone === 'default' ? undefined : life.tone} label={life.label} />
              {med && (
                <Tooltip title={med.help} arrow>
                  <Chip size="small" variant="outlined" color={med.tone} label={med.label} />
                </Tooltip>
              )}
              <Chip
                size="small"
                variant="outlined"
                label={`total lead ${humanizeLead(path.total_lag_bins, interval)}`}
              />
            </Stack>

            {/* Narrative */}
            {path.narrative && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                {path.narrative}
              </Typography>
            )}

            <Divider sx={{ mb: 2 }} />

            {/* Hop cards */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              The chain, hop by hop
            </Typography>
            <Stack spacing={0} alignItems="stretch">
              {hops.map((hop, i) => (
                <React.Fragment key={`${hop.source_key}-${hop.target_key}`}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }} title={`${hop.source_key} → ${hop.target_key}`}>
                          {nameOf(hop.source_key)}
                          <Typography component="span" variant="body2" color="text.secondary">
                            {' → '}{nameOf(hop.target_key)}
                          </Typography>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          leads by {hop.lag_bins} bin{hop.lag_bins === 1 ? '' : 's'}
                          {interval ? ` (~${humanizeLead(hop.lag_bins, interval)})` : ''}
                        </Typography>
                      </Stack>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={hop.strength != null ? `lift ${(hop.strength * 100).toFixed(1)}%` : '—'}
                      />
                    </Stack>
                  </Paper>
                  {i < hops.length - 1 && (
                    <Stack alignItems="center" sx={{ py: 0.25 }}>
                      <ArrowDownwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    </Stack>
                  )}
                </React.Fragment>
              ))}
              <Stack alignItems="center" sx={{ py: 0.25 }}>
                <ArrowDownwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              </Stack>
              <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'primary.main' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FlagIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ fontWeight: 700 }} title={goal?.target_key}>
                    {nameOf(goal?.target_key)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    your goal
                  </Typography>
                </Stack>
              </Paper>
            </Stack>

            {/* Mediation evidence */}
            {path.mediation_evidence && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Mediation test
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Does {nameOf(hops[0]?.source_key)} still add forecasting lift once{' '}
                  {nameOf(path.mediation_evidence.mediator_key)} is already in the model?
                </Typography>
                <Stack direction="row" spacing={2.5}>
                  <Stack>
                    <Typography variant="caption" color="text.secondary">Lift given mediator</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {(100 * (path.mediation_evidence.lift_given_mediator || 0)).toFixed(2)}%
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="caption" color="text.secondary">p-value</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {path.mediation_evidence.p ?? '—'}
                    </Typography>
                  </Stack>
                  {path.confidence?.min_hop_q != null && (
                    <Stack>
                      <Typography variant="caption" color="text.secondary">Weakest hop q</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {path.confidence.min_hop_q}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default PathDrawer;
